import React, { useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Download } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Dot,
  ReferenceLine,
} from "recharts";

export const EMOTION_COLORS = {
  happy: "#FFB74D",
  sad: "#64B5F6",
  angry: "#FF6B6B",
  fear: "#A78BFA",
  surprise: "#4DD0E1",
  disgust: "#81C784",
  neutral: "#9E9E9E",
};

const fmt = (sec) => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const CustomDot = (props) => {
  const { cx, cy, payload, onSeek } = props;
  if (cx == null || cy == null) return null;
  const color = EMOTION_COLORS[payload.emotion?.toLowerCase()] || "#666";
  return (
    <g
      style={{ cursor: onSeek ? "pointer" : "default" }}
      onClick={() => onSeek && onSeek(payload.time)}
    >
      {/* Invisible larger hit target */}
      <circle cx={cx} cy={cy} r={14} fill="transparent" />
      <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={1.5} />
    </g>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const color = EMOTION_COLORS[p.emotion?.toLowerCase()] || "#666";
  return (
    <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200 min-w-[140px]">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400 mb-1">
        {fmt(p.time)}
      </div>
      <div className="flex items-center gap-2">
        <span
          className="w-2 h-2 rounded-full"
          style={{ background: color }}
        />
        <span className="text-sm font-medium text-gray-900 capitalize">
          {p.emotion}
        </span>
      </div>
      <div className="text-xs text-gray-500 mt-1 font-light">
        Confidence{" "}
        <span className="text-gray-900 font-mono">
          {Math.round(p.confidence)}%
        </span>
      </div>
    </div>
  );
};

const EmotionArc = ({ timeline, onSeek, currentTime }) => {
  const reportRef = useRef(null);

  if (!timeline || timeline.length === 0) return null;

  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    try {
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [canvas.width / 2, canvas.height / 2],
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width / 2, canvas.height / 2);
      const stamp = new Date().toISOString().slice(0, 10);
      pdf.save(`emotion-arc-report-${stamp}.pdf`);
    } catch (e) {
      console.error("Export failed", e);
    }
  };

  // Build segments where consecutive frames share an emotion (for the ribbon)
  const segments = [];
  timeline.forEach((f, i) => {
    const emo = f.emotion?.toLowerCase() || "unknown";
    const last = segments[segments.length - 1];
    if (last && last.emotion === emo) {
      last.end = f.time;
      last.count += 1;
    } else {
      segments.push({
        emotion: emo,
        start: f.time,
        end: f.time,
        count: 1,
      });
    }
  });
  const totalDuration =
    timeline[timeline.length - 1].time - timeline[0].time || 1;

  // For unique emotion legend
  const uniqueEmotions = [
    ...new Set(timeline.map((f) => f.emotion?.toLowerCase())),
  ];

  // Build chapters — segments where same emotion persists for at least 5 seconds
  const CHAPTER_MIN_DURATION = 5;
  const chapters = segments
    .filter((seg) => seg.end - seg.start >= CHAPTER_MIN_DURATION)
    .map((seg, i) => ({
      ...seg,
      idx: i + 1,
    }));

  return (
    <div className="mt-6 pt-6 border-t border-dashed border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">
          Emotion arc
        </div>
        <div className="flex items-center gap-3">
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {uniqueEmotions.map((emo) => (
              <span
                key={emo}
                className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-gray-600"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: EMOTION_COLORS[emo] || "#666" }}
                />
                {emo}
              </span>
            ))}
          </div>
          <button
            type="button"
            data-testid="export-pdf-btn"
            onClick={handleExportPDF}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-gray-700 bg-white hover:bg-gray-900 hover:text-white border border-gray-300 hover:border-gray-900 rounded-md transition-all"
            title="Export emotion analysis as PDF"
          >
            <Download className="w-3 h-3" />
            Export PDF
          </button>
        </div>
      </div>

      <div ref={reportRef} className="bg-white p-2 rounded-xl">

      {/* Line chart */}
      <div className="h-[180px] w-full bg-gray-50 rounded-xl p-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={timeline}
            margin={{ top: 10, right: 16, bottom: 10, left: 0 }}
          >
            <CartesianGrid strokeDasharray="2 4" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="time"
              type="number"
              domain={["dataMin", "dataMax"]}
              tickFormatter={fmt}
              tick={{ fontSize: 10, fill: "#9ca3af", fontFamily: "monospace" }}
              stroke="#d1d5db"
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 10, fill: "#9ca3af", fontFamily: "monospace" }}
              stroke="#d1d5db"
              tickLine={false}
              width={42}
            />
            <ReferenceLine y={50} stroke="#e5e7eb" strokeDasharray="2 4" />
            {currentTime != null && currentTime >= 0 && (
              <ReferenceLine
                x={currentTime}
                stroke="#FF3366"
                strokeWidth={1.5}
                label={{
                  value: fmt(currentTime),
                  position: "top",
                  fill: "#FF3366",
                  fontSize: 10,
                  fontFamily: "monospace",
                }}
              />
            )}
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#9ca3af", strokeWidth: 1, strokeDasharray: "3 3" }} />
            <Line
              type="monotone"
              dataKey="confidence"
              stroke="#111"
              strokeWidth={1.5}
              dot={<CustomDot onSeek={onSeek} />}
              activeDot={{ r: 7, strokeWidth: 2, cursor: onSeek ? "pointer" : "default" }}
              animationDuration={800}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Emotion ribbon — temporal sequence of emotion segments */}
      <div className="mt-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 mb-2">
          Sequence
        </div>
        <div className="relative flex h-3 rounded-full overflow-hidden bg-gray-100" data-testid="emotion-ribbon">
          {segments.map((seg, i) => {
            const width =
              ((seg.end - seg.start + 2) / (totalDuration + 2)) * 100;
            return (
              <button
                key={i}
                type="button"
                onClick={() => onSeek && onSeek(seg.start)}
                title={`${seg.emotion} · ${fmt(seg.start)} → ${fmt(seg.end)}${onSeek ? " · click to jump" : ""}`}
                data-testid={`ribbon-segment-${i}`}
                style={{
                  width: `${width}%`,
                  background: EMOTION_COLORS[seg.emotion] || "#666",
                  cursor: onSeek ? "pointer" : "default",
                }}
                className="block h-full transition-all hover:opacity-80 hover:scale-y-150 focus:outline-none focus:ring-2 focus:ring-offset-1"
              />
            );
          })}
          {currentTime != null && currentTime >= 0 && (
            <div
              className="absolute top-[-3px] bottom-[-3px] w-[2px] bg-[#FF3366] pointer-events-none transition-all duration-150"
              style={{
                left: `${Math.min(
                  100,
                  Math.max(
                    0,
                    ((currentTime - timeline[0].time) / (totalDuration + 2)) * 100
                  )
                )}%`,
              }}
              data-testid="ribbon-cursor"
            >
              <div className="absolute -top-1 -left-[3px] w-2 h-2 rounded-full bg-[#FF3366]" />
            </div>
          )}
        </div>
        <div className="flex justify-between mt-1.5 text-[9px] font-mono text-gray-400">
          <span>{fmt(timeline[0].time)}</span>
          <span>{fmt(timeline[timeline.length - 1].time)}</span>
        </div>
      </div>

      {/* Auto-generated chapters */}
      {chapters.length > 0 && (
        <div className="mt-6 pt-5 border-t border-dashed border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">
              Auto chapters
            </div>
            <div className="text-[10px] font-mono text-gray-400">
              {chapters.length} chapter{chapters.length !== 1 ? "s" : ""}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {chapters.map((ch) => {
              const color = EMOTION_COLORS[ch.emotion] || "#666";
              const duration = Math.round(ch.end - ch.start);
              return (
                <button
                  key={ch.idx}
                  type="button"
                  onClick={() => onSeek && onSeek(ch.start)}
                  data-testid={`chapter-${ch.idx}`}
                  className="group flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all text-left"
                  style={{ cursor: onSeek ? "pointer" : "default" }}
                >
                  <span
                    className="font-mono text-[10px] tracking-wider uppercase w-7 flex-shrink-0"
                    style={{ color }}
                  >
                    Ch{String(ch.idx).padStart(2, "0")}
                  </span>
                  <span
                    className="w-1 self-stretch rounded-full flex-shrink-0"
                    style={{ background: color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 capitalize leading-tight">
                      {ch.emotion}
                    </div>
                    <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                      {fmt(ch.start)} → {fmt(ch.end)}{" "}
                      <span className="text-gray-300">·</span>{" "}
                      {duration}s
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default EmotionArc;
